import React, { useCallback, useRef, useState } from 'react';
import {
    ApplyResult,
    ImportPlan,
    StageRawResult,
    applyImportPlan,
    fetchMatchBaseline,
    fetchUnresolvedRaw,
    parseLeadScoutExport,
    planLeadImport,
    stageRawFromFile,
} from '../../services/leadImport';
import { currentCompanyId } from '../../services/leadService';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import {
    AlertTriangle, CheckCircle2, Database, DownloadCloud, FileUp, GitMerge, Loader2, PlusCircle,
    Radar, Trash2, X,
} from 'lucide-react';

/**
 * UI-Auslöser für den Lead-Import (Datei/leads_raw → leads / tender_opportunities).
 *
 * Bewusst ein reiner Container (Standing Decision: dünne Container, Logik in
 * puren/IO-Funktionen). Die gesamte Entity-Resolution steckt in leadImport.ts;
 * hier wird nur gewählt, gestaged, angezeigt und ausgeführt — keine
 * Geschäftslogik.
 *
 * Primärer Weg „Aus Datei": der Lead-Scout schreibt nicht mehr selbst in die
 * Datenbank, sondern exportiert eine JSON-Datei. Diese wird hier hochgeladen,
 * unter der Nutzer-Session in leads_raw abgelegt (stageRawFromFile) und danach
 * EXAKT wie bisher geplant/ausgeführt. Der zweite Weg „Aus leads_raw" bleibt für
 * den optionalen Worker/Edge-Betrieb erhalten.
 */
interface Props {
    onClose: () => void;
    /** Nach erfolgreichem Import: der Aufrufer lädt die Pipeline neu. */
    onImported: () => void;
    /** „Zuordnung prüfen": das bestehende Review-Modal öffnen. */
    onReviewRequested: () => void;
}

/** Eine Kennzahl der Vorschau. */
const Kennzahl: React.FC<{
    icon: React.ReactNode;
    zahl: number;
    label: string;
    ton: string;
}> = ({ icon, zahl, label, ton }) => (
    <div className={`rounded-2xl border p-4 flex items-center gap-3 ${ton}`}>
        <div className="shrink-0">{icon}</div>
        <div>
            <p className="text-2xl font-bold leading-none">{zahl}</p>
            <p className="text-xs font-medium mt-1">{label}</p>
        </div>
    </div>
);

type Modus = 'start' | 'db' | 'datei';

export const LeadImportDialog: React.FC<Props> = ({ onClose, onImported, onReviewRequested }) => {
    const [modus, setModus] = useState<Modus>('start');
    const [plan, setPlan] = useState<ImportPlan | null>(null);
    const [rohAnzahl, setRohAnzahl] = useState(0);
    const [laedt, setLaedt] = useState(false);
    const [staging, setStaging] = useState(false);
    const [stageInfo, setStageInfo] = useState<StageRawResult | null>(null);
    const [importiert, setImportiert] = useState(false);
    const [ergebnis, setErgebnis] = useState<ApplyResult | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);

    const dateiInput = useRef<HTMLInputElement>(null);

    // ─── Vorschau (gemeinsam für beide Wege) ─────────────────────────────────
    // Rohzeilen und Bestand parallel — nacheinander wäre die Vorschau doppelt
    // so lange leer. fetchMatchBaseline liefert genau die für planLeadImport
    // nötigen Bestandsdaten; nichts wird doppelt abgefragt.
    const vorschauLaden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            const [raws, baseline] = await Promise.all([
                fetchUnresolvedRaw(),
                fetchMatchBaseline(),
            ]);
            setRohAnzahl(raws.length);
            setPlan(planLeadImport(raws, baseline.leads, baseline.customers));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, []);

    const ausLeadsRaw = () => {
        setModus('db');
        void vorschauLaden();
    };

    // ─── Datei wählen → nach leads_raw stagen → Vorschau ─────────────────────
    const dateiGewaehlt = async (file: File | undefined) => {
        if (!file) return;
        setModus('datei');
        setFehler(null);
        setStageInfo(null);
        setPlan(null);
        setStaging(true);
        try {
            const text = await file.text();
            const parsed = parseLeadScoutExport(text);
            const companyId = await currentCompanyId();
            const info = await stageRawFromFile(parsed.records, parsed.source, companyId);
            setStageInfo(info);
            // Ab hier genau der bestehende Fluss: die frisch gestageten Zeilen
            // erscheinen in fetchUnresolvedRaw und werden wie immer geplant.
            await vorschauLaden();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setStaging(false);
        }
    };

    // ─── Ausführen ───────────────────────────────────────────────────────────
    const importieren = async () => {
        if (!plan) return;
        setImportiert(true);
        setFehler(null);
        try {
            const companyId = await currentCompanyId();
            const res = await applyImportPlan(plan, companyId);
            setErgebnis(res);
            // Die neuen Leads sollen sofort in der Pipeline erscheinen.
            onImported();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setImportiert(false);
        }
    };

    const beschaeftigt = laedt || staging || importiert;
    const nichtsGefunden = modus !== 'start' && !laedt && !staging && !fehler && plan != null && rohAnzahl === 0;
    const zuPruefen = ergebnis?.zurReview ?? 0;

    return (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-500/20 p-2 rounded-xl border border-indigo-500/30">
                            <DownloadCloud className="w-5 h-5 text-indigo-300" />
                        </div>
                        <div>
                            <h3 className="font-bold">Neue Treffer importieren</h3>
                            <p className="text-xs text-slate-400">
                                Funde des Lead-Scouts als Leads übernehmen und automatisch abgleichen.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-all">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <input
                    ref={dateiInput}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={(e) => { void dateiGewaehlt(e.target.files?.[0]); e.target.value = ''; }}
                />

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {fehler && (
                        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{fehler}
                        </div>
                    )}

                    {modus === 'start' ? (
                        // ─── Quelle wählen ──────────────────────────────────────
                        <div className="space-y-3">
                            <p className="text-sm text-slate-600">
                                Woher sollen die neuen Treffer kommen?
                            </p>
                            <button
                                type="button"
                                onClick={() => dateiInput.current?.click()}
                                className="w-full text-left rounded-2xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 p-4 flex items-start gap-3 transition-all"
                            >
                                <div className="bg-indigo-50 text-indigo-600 p-2 rounded-xl shrink-0">
                                    <FileUp className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-bold text-slate-800">Aus Datei importieren</p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Die vom Lead-Scout exportierte JSON-Datei hochladen. Empfohlener Weg —
                                        der Scraper braucht keinen Datenbankzugang.
                                    </p>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={ausLeadsRaw}
                                className="w-full text-left rounded-2xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 p-4 flex items-start gap-3 transition-all"
                            >
                                <div className="bg-slate-100 text-slate-600 p-2 rounded-xl shrink-0">
                                    <Database className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-bold text-slate-800">Aus leads_raw</p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Bereits in der Datenbank liegende Rohdaten verarbeiten — falls ein
                                        Worker oder eine Edge-Funktion sie befüllt hat.
                                    </p>
                                </div>
                            </button>
                        </div>
                    ) : staging ? (
                        <p className="text-sm text-slate-500 py-12 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                            Datei wird eingelesen und abgelegt…
                        </p>
                    ) : laedt ? (
                        <p className="text-sm text-slate-500 py-12 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                            Neue Treffer werden geprüft…
                        </p>
                    ) : nichtsGefunden ? (
                        <div className="py-12 text-center text-slate-400">
                            <Radar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                            <p className="font-bold text-slate-600">Keine neuen Treffer.</p>
                            {stageInfo ? (
                                <p className="text-xs max-w-sm mx-auto mt-1">
                                    {stageInfo.angelegt} neu abgelegt, {stageInfo.uebersprungen} schon vorhanden —
                                    alle sind bereits zugeordnet oder geprüft.
                                </p>
                            ) : (
                                <p className="text-xs max-w-sm mx-auto mt-1">
                                    Der Lead-Scout legt neue Funde in leads_raw ab — läuft aktuell keiner,
                                    bleibt hier alles leer.
                                </p>
                            )}
                        </div>
                    ) : ergebnis ? (
                        // ─── Ergebnis nach dem Import ───────────────────────────
                        <div className="space-y-4">
                            <div className="py-4 text-center">
                                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                                <p className="font-bold text-slate-700">Import abgeschlossen.</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Kennzahl
                                    icon={<PlusCircle className="w-6 h-6 text-emerald-600" />}
                                    zahl={ergebnis.angelegt}
                                    label="neue Leads angelegt"
                                    ton="bg-emerald-50 border-emerald-100 text-emerald-800"
                                />
                                <Kennzahl
                                    icon={<GitMerge className="w-6 h-6 text-blue-600" />}
                                    zahl={ergebnis.zugeordnet}
                                    label="automatisch zugeordnet"
                                    ton="bg-blue-50 border-blue-100 text-blue-800"
                                />
                                <Kennzahl
                                    icon={<AlertTriangle className="w-6 h-6 text-amber-600" />}
                                    zahl={ergebnis.zurReview}
                                    label="brauchen Prüfung"
                                    ton="bg-amber-50 border-amber-100 text-amber-800"
                                />
                                <Kennzahl
                                    icon={<DownloadCloud className="w-6 h-6 text-indigo-600" />}
                                    zahl={ergebnis.ausschreibungen}
                                    label="Ausschreibungen"
                                    ton="bg-indigo-50 border-indigo-100 text-indigo-800"
                                />
                            </div>

                            {ergebnis.fehler.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                                    <p className="text-sm font-bold text-red-800 flex items-center gap-2 mb-1">
                                        <AlertTriangle className="w-4 h-4" />
                                        {ergebnis.fehler.length} Datensatz{ergebnis.fehler.length === 1 ? '' : 'e'} mit Fehler
                                    </p>
                                    <ul className="text-xs text-red-700 space-y-0.5 list-disc pl-5">
                                        {ergebnis.fehler.slice(0, 5).map((f) => (
                                            <li key={f.rawId}>{f.meldung}</li>
                                        ))}
                                    </ul>
                                    {ergebnis.fehler.length > 5 && (
                                        <p className="text-xs text-red-600 mt-1">und {ergebnis.fehler.length - 5} weitere…</p>
                                    )}
                                </div>
                            )}

                            {zuPruefen > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <p className="text-sm text-amber-900">
                                        <span className="font-bold">{zuPruefen} Fall{zuPruefen === 1 ? '' : 'e'}</span>{' '}
                                        {zuPruefen === 1 ? 'braucht' : 'brauchen'} deine Entscheidung.
                                    </p>
                                    <Button
                                        variant="primary"
                                        onClick={onReviewRequested}
                                        icon={<GitMerge className="w-4 h-4" />}
                                    >
                                        Zuordnung prüfen
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : plan ? (
                        // ─── Vorschau vor dem Import ────────────────────────────
                        <div className="space-y-4">
                            {stageInfo && (
                                <div className="bg-indigo-50 border border-indigo-100 text-indigo-900 rounded-xl p-3 text-sm flex items-start gap-2">
                                    <FileUp className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>
                                        {stageInfo.angelegt} neu aus der Datei abgelegt
                                        {stageInfo.uebersprungen > 0 && `, ${stageInfo.uebersprungen} schon vorhanden`}.
                                    </span>
                                </div>
                            )}
                            <p className="text-sm text-slate-600">
                                {rohAnzahl} neue{rohAnzahl === 1 ? 'r Treffer' : ' Treffer'} gefunden. So würde der
                                Import sie verarbeiten:
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <Kennzahl
                                    icon={<PlusCircle className="w-6 h-6 text-emerald-600" />}
                                    zahl={plan.neu.length}
                                    label="werden als neuer Lead angelegt"
                                    ton="bg-emerald-50 border-emerald-100 text-emerald-800"
                                />
                                <Kennzahl
                                    icon={<GitMerge className="w-6 h-6 text-blue-600" />}
                                    zahl={plan.zugeordnet.length}
                                    label="werden automatisch zugeordnet"
                                    ton="bg-blue-50 border-blue-100 text-blue-800"
                                />
                                <Kennzahl
                                    icon={<AlertTriangle className="w-6 h-6 text-amber-600" />}
                                    zahl={plan.offen.length}
                                    label="brauchen manuelle Prüfung"
                                    ton="bg-amber-50 border-amber-100 text-amber-800"
                                />
                                <Kennzahl
                                    icon={<DownloadCloud className="w-6 h-6 text-indigo-600" />}
                                    zahl={plan.ausschreibungen.length}
                                    label="Ausschreibungen"
                                    ton="bg-indigo-50 border-indigo-100 text-indigo-800"
                                />
                            </div>
                            {plan.verworfen.length > 0 && (
                                <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                    <Trash2 className="w-3.5 h-3.5 shrink-0" />
                                    {plan.verworfen.length} Treffer ohne verwertbaren Inhalt werden übersprungen.
                                </p>
                            )}
                        </div>
                    ) : null}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-end items-center gap-3 shrink-0">
                    {modus === 'start' ? (
                        <Button variant="ghost" onClick={onClose}>Schließen</Button>
                    ) : ergebnis || nichtsGefunden ? (
                        <Button variant="ghost" onClick={onClose}>Schließen</Button>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={onClose} disabled={importiert}>Abbrechen</Button>
                            <Button
                                variant="primary"
                                onClick={() => void importieren()}
                                disabled={beschaeftigt || !plan || rohAnzahl === 0}
                                icon={importiert
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <DownloadCloud className="w-4 h-4" />}
                            >
                                Importieren
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
